// src/store/userStore.js
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { auth, signInWithPopup, provider, signOut } from '../config/firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import {
  getValidToken,
  syncTokenWithCookie,
  fetchWithToken,
  clearAuthCookie,
} from '../utils/tokenManager';
// Import cartStore for logout handling
import useCartStore from './cartStore';

// Constants
const MIN_VERIFICATION_INTERVAL = 5 * 60 * 1000; // 5 minutes
const MIN_PROFILE_FETCH_INTERVAL = 60 * 1000;     // 1 minute

// State variables that don't need to be in the store
let verificationInProgress = false;
let lastVerificationTime = 0;
let lastProfileFetchTime = 0;
let verificationDone = false;

// Initialize verification status from sessionStorage
if (typeof window !== 'undefined') {
  try {
    verificationDone = sessionStorage.getItem('backend_verified') === 'true';
  } catch (e) {
    console.error('Failed to read from sessionStorage:', e);
  }
}

// Main user store
const useUserStore = create(
  persist(
    (set, get) => ({
      // User state
      user: null,
      isLoggedIn: false,
      loading: true,
      termsAccepted: false,
      error: null,
      accessToken: null,
      verifiedWithBackend: false,
      profileData: null,
      profileFetched: false,
      is_admin: false, // Added is_admin field

      // Subscription data
      isSubscriptionActive: false,
      subscription: '',
      subscriptionEnergy: 0,
      permanentEnergy: 0,
      purchases: [],
      stripeCustomerId: '',

      // Token management
      refreshTokenIfNeeded: async (force = false) => {
        const token = await getValidToken(force);
        if (token) {
          set({ accessToken: token });
        }
        return token;
      },

      // Enhanced fetch wrapper with token refresh
      fetchWithTokenRefresh: async (url, options = {}) => {
        return fetchWithToken(url, options);
      },

      // Get token
      getFirebaseToken: async (forceRefresh = false) => {
        return getValidToken(forceRefresh);
      },

      // Fetch user profile from backend
      fetchUserProfile: async (forceRefresh = false) => {
        if (!get().isLoggedIn) {
          return null;
        }

        const now = Date.now();
        if (!forceRefresh && now - lastProfileFetchTime < MIN_PROFILE_FETCH_INTERVAL) {
          return get().profileData;
        }

        try {
          lastProfileFetchTime = now;
          const profileUrl = `${process.env.NEXT_PUBLIC_BACKEND_URL}/user_profile`;
          const response = await get().fetchWithTokenRefresh(profileUrl);

          if (!response.ok) {
            const errorText = await response.text();
            console.error('Failed to fetch user profile:', errorText);
            return null;
          }

          const profileData = await response.json();

          set({
            profileData,
            profileFetched: true,
            user: {
              ...get().user,
              displayName: profileData.name || get().user?.displayName || profileData.email,
              photoURL: profileData.photo_url || profileData.avatar_url || get().user?.photoURL,
            },
            termsAccepted: profileData.is_terms_accepted,
            isLoggedIn: true,
            isSubscriptionActive: profileData.is_subscription_active || false,
            subscription: profileData.subscription || '',
            subscriptionEnergy: profileData.subscription_energy || 0,
            permanentEnergy: profileData.permanent_energy || 0,
            purchases: profileData.purchases || [],
            stripeCustomerId: profileData.stripe_customer_id || '',
            is_admin: profileData.is_admin || false, // Set is_admin from profile data
          });

          return profileData;
        } catch (error) {
          console.error('Error fetching user profile:', error);
          return null;
        }
      },

      // Subscribe to Firebase auth state changes
      subscribeAuthChanges: () => {
        return onAuthStateChanged(auth, async (firebaseUser) => {
          if (firebaseUser) {
            try {
              if (typeof window !== 'undefined') {
                try {
                  verificationDone = sessionStorage.getItem('backend_verified') === 'true';
                } catch (e) {
                  // Silent catch
                }
              }

              set({
                isLoggedIn: true,
                user: {
                  uid: firebaseUser.uid,
                  email: firebaseUser.email,
                  displayName: firebaseUser.displayName || firebaseUser.email,
                  photoURL: firebaseUser.photoURL,
                },
              });

              const token = await getValidToken(true);
              if (token) {
                set({ accessToken: token });
                await syncTokenWithCookie(token);
              }

              const now = Date.now();
              const needsBackendVerification =
                !verificationDone &&
                !get().verifiedWithBackend &&
                !verificationInProgress &&
                now - lastVerificationTime > MIN_VERIFICATION_INTERVAL;

              if (!needsBackendVerification) {
                const currentUser = get().user || {};
                const updatedUserData = {
                  ...currentUser,
                  uid: firebaseUser.uid,
                  email: firebaseUser.email,
                  displayName: currentUser.displayName || firebaseUser.displayName || firebaseUser.email,
                  photoURL: currentUser.photoURL || firebaseUser.photoURL,
                };

                set({
                  user: updatedUserData,
                  isLoggedIn: true,
                  loading: false,
                  error: null,
                });

                await get().fetchUserProfile(true);
                return;
              }

              verificationInProgress = true;
              lastVerificationTime = now;

              const { exists, error } = await verifyUserWithRetry(token);
              verificationInProgress = false;

              if (exists) {
                const userData = {
                  uid: firebaseUser.uid,
                  email: firebaseUser.email,
                  displayName: firebaseUser.displayName || firebaseUser.email,
                  photoURL: firebaseUser.photoURL,
                };

                verificationDone = true;
                if (typeof window !== 'undefined') {
                  try {
                    sessionStorage.setItem('backend_verified', 'true');
                  } catch (e) {
                    console.error('Failed to write to sessionStorage:', e);
                  }
                }

                set({
                  user: userData,
                  isLoggedIn: true,
                  loading: false,
                  error: null,
                  verifiedWithBackend: true,
                });

                await get().fetchUserProfile(true);
              } else {
                set({
                  user: null,
                  isLoggedIn: false,
                  loading: false,
                  verifiedWithBackend: false,
                  accessToken: null,
                  error: error || 'User not registered in application database',
                });
              }
            } catch (error) {
              verificationInProgress = false;
              set({
                user: null,
                isLoggedIn: false,
                loading: false,
                verifiedWithBackend: false,
                accessToken: null,
                error: 'Failed to authenticate with server: ' + error.message,
              });
            }
          } else {
            await clearAuthCookie();
            set({
              user: null,
              isLoggedIn: false,
              loading: false,
              verifiedWithBackend: false,
              accessToken: null,
              profileData: null,
              profileFetched: false,
              isSubscriptionActive: false,
              subscription: '',
              subscriptionEnergy: 0,
              permanentEnergy: 0,
              purchases: [],
              stripeCustomerId: '',
              is_admin: false, // Reset is_admin on logout
              error: null,
            });

            verificationInProgress = false;
            verificationDone = false;
            if (typeof window !== 'undefined') {
              try {
                sessionStorage.removeItem('backend_verified');
              } catch (e) {
                // Silent catch
              }
            }
          }
        });
      },

      // Login with Google
      loginWithGoogle: async () => {
        set({ loading: true, error: null, verifiedWithBackend: false });
        try {
          verificationInProgress = false;
          lastVerificationTime = 0;
          verificationDone = false;

          if (typeof window !== 'undefined') {
            try {
              sessionStorage.removeItem('backend_verified');
            } catch (e) {
              // Silent catch
            }
          }

          await signInWithPopup(auth, provider);
        } catch (error) {
          set({
            loading: false,
            error: error.message,
          });
        }
      },

      // Logout with thorough cleanup
      logout: async () => {
        const currentFunctions = preserveFunctions(get());

        // Set loading state
        set({
          loading: true,
          error: null,
          ...currentFunctions,
        });

        try {
          // ВАЖНО: Сначала очищаем авторизацию и куки ПЕРЕД работой с корзиной
          await signOut(auth);
          await clearAuthCookie();
          clearLocalStorage();
          sessionStorage.clear();
          resetTrackingVariables();

          // Обновляем состояние после очистки авторизации
          set({
            user: null,
            isLoggedIn: false,
            verifiedWithBackend: false,
            accessToken: null,
            profileData: null,
            profileFetched: false,
            isSubscriptionActive: false,
            subscription: '',
            subscriptionEnergy: 0,
            permanentEnergy: 0,
            purchases: [],
            stripeCustomerId: '',
            is_admin: false, // Reset is_admin on logout
            error: null,
            ...currentFunctions,
          });

          // ТОЛЬКО ПОСЛЕ очистки авторизации работаем с корзиной
          if (typeof window !== 'undefined') {
            try {
              await useCartStore.getState().handleLogout();
            } catch (error) {
              console.error('Error handling cart during logout:', error);
            }
          }

          // Завершаем процесс выхода
          set({
            loading: false,
            ...currentFunctions,
          });
        } catch (error) {
          console.error('Logout error:', error);
          
          // Даже в случае ошибки, очищаем состояние и пытаемся обработать корзину
          set({
            user: null,
            isLoggedIn: false,
            loading: false,
            verifiedWithBackend: false,
            accessToken: null,
            profileData: null,
            profileFetched: false,
            isSubscriptionActive: false,
            subscription: '',
            subscriptionEnergy: 0,
            permanentEnergy: 0,
            purchases: [],
            stripeCustomerId: '',
            is_admin: false, // Reset is_admin on logout
            error: 'Logout error: ' + error.message,
            ...currentFunctions,
          });
          
          // Попытка работы с корзиной даже при ошибке выхода
          if (typeof window !== 'undefined') {
            try {
              await useCartStore.getState().handleLogout();
            } catch (cartError) {
              console.error('Error handling cart during logout error recovery:', cartError);
            }
          }
        }
      },

      // Reset loading state
      resetLoadingState: () => set({ loading: false }),

      // Emergency reset function
      resetStore: () => {
        const currentFunctions = preserveFunctions(get());

        try {
          // Сначала очищаем авторизацию
          signOut(auth).catch((err) => null);
          clearAuthCookie().catch((err) => null);
          
          // Затем очищаем хранилище
          try {
            localStorage.clear();
            sessionStorage.clear();
          } catch (e) {
            // no-op
          }
          
          resetTrackingVariables();

          const initialState = {
            user: null,
            isLoggedIn: false,
            loading: false,
            termsAccepted: false,
            verifiedWithBackend: false,
            accessToken: null,
            profileData: null,
            profileFetched: false,
            isSubscriptionActive: false,
            subscription: '',
            subscriptionEnergy: 0,
            permanentEnergy: 0,
            purchases: [],
            stripeCustomerId: '',
            is_admin: false, // Reset is_admin on store reset
            error: null,
            ...currentFunctions,
          };

          set(initialState);
          
          // ПОСЛЕ очистки авторизации работаем с корзиной
          if (typeof window !== 'undefined') {
            try {
              useCartStore.getState().handleLogout();
            } catch (error) {
              console.error('Error handling cart during resetStore:', error);
            }
          }

          if (typeof window !== 'undefined') {
            window.location.reload();
          }
        } catch (e) {
          console.error("Severe error during reset:", e);
          if (typeof window !== 'undefined') {
            window.location.reload();
          }
        }
      },

      // Accept terms
      acceptTerms: () => set({ termsAccepted: true }),

      // Debug helper
      checkCurrentAuth: () => {
        const currentUser = auth.currentUser;
        const storeState = get();
        return {
          firebaseUser: currentUser
            ? {
                uid: currentUser.uid,
                email: currentUser.email,
                displayName: currentUser.displayName,
              }
            : null,
          storeState,
        };
      },

      // Set user manually (for testing/debugging)
      setUserManually: (userData) => {
        set({
          user: userData,
          isLoggedIn: !!userData,
          loading: false,
          verifiedWithBackend: !!userData,
          error: null,
        });
      },

      // SUBSCRIPTION HELPERS
      hasActiveSubscription: () => {
        return get().isSubscriptionActive;
      },

      getTotalEnergy: () => {
        return get().subscriptionEnergy + get().permanentEnergy;
      },

      hasPurchased: (purchaseId) => {
        const purchases = get().purchases || [];
        return purchases.includes(purchaseId);
      },

      updateSubscriptionStatus: (isActive, subscriptionData = '', subscriptionEnergy = 0) => {
        set({
          isSubscriptionActive: isActive,
          subscription: subscriptionData,
          subscriptionEnergy,
        });

        const currentProfileData = get().profileData;
        if (currentProfileData) {
          set({
            profileData: {
              ...currentProfileData,
              is_subscription_active: isActive,
              subscription: subscriptionData,
              subscription_energy: subscriptionEnergy,
            },
          });
        }
      },

      addPurchase: (purchaseId) => {
        const currentPurchases = get().purchases || [];
        if (!currentPurchases.includes(purchaseId)) {
          const updatedPurchases = [...currentPurchases, purchaseId];
          set({ purchases: updatedPurchases });

          const currentProfileData = get().profileData;
          if (currentProfileData) {
            set({
              profileData: {
                ...currentProfileData,
                purchases: updatedPurchases,
              },
            });
          }
        }
      },

      // Check if user is admin
      isAdmin: () => {
        return get().is_admin;
      },

      checkAccessWithProfileRefresh: async (assetId, collection) => {
        const { isLoggedIn, profileFetched, purchases } = get();

        if (!isLoggedIn) {
          return false;
        }

        const profileData = await get().fetchUserProfile(true);

        if (!profileData) {
          return (
            checkPurchaseIncludes(purchases, assetId) ||
            checkPurchaseIncludes(purchases, collection, true) ||
            get().isSubscriptionActive
          );
        }

        const updatedPurchases = get().purchases || [];

        const hasAsset = checkPurchaseIncludes(updatedPurchases, assetId);
        const hasCollection = checkPurchaseIncludes(updatedPurchases, collection, true);

        return hasAsset || hasCollection || get().isSubscriptionActive;
      },
    }),
    {
      name: 'user-storage',
      storage: createJSONStorage(() => localStorage),
      // Only persist essential data
      partialize: (state) => ({
        termsAccepted: state.termsAccepted,
        verifiedWithBackend: state.verifiedWithBackend,
        profileFetched: state.profileFetched,
      }),
    }
  )
);

// HELPER FUNCTIONS
function preserveFunctions(state) {
  return {
    subscribeAuthChanges: state.subscribeAuthChanges,
    resetLoadingState: state.resetLoadingState,
    acceptTerms: state.acceptTerms,
    loginWithGoogle: state.loginWithGoogle,
    logout: state.logout,
    resetStore: state.resetStore,
    checkCurrentAuth: state.checkCurrentAuth,
    setUserManually: state.setUserManually,
    fetchUserProfile: state.fetchUserProfile,
    getFirebaseToken: state.getFirebaseToken,
    refreshTokenIfNeeded: state.refreshTokenIfNeeded,
    fetchWithTokenRefresh: state.fetchWithTokenRefresh,
    hasActiveSubscription: state.hasActiveSubscription,
    getTotalEnergy: state.getTotalEnergy,
    hasPurchased: state.hasPurchased,
    updateSubscriptionStatus: state.updateSubscriptionStatus,
    addPurchase: state.addPurchase,
    isAdmin: state.isAdmin, // Add isAdmin function to preserved functions
    checkAccessWithProfileRefresh: state.checkAccessWithProfileRefresh,
  };
}

function resetTrackingVariables() {
  verificationInProgress = false;
  lastVerificationTime = 0;
  verificationDone = false;
  lastProfileFetchTime = 0;
}

function clearLocalStorage() {
  localStorage.removeItem('firebase:authUser');
  localStorage.removeItem('firebase:token');
  localStorage.removeItem('firebaseui:rememberedAccounts');
  localStorage.removeItem('user-storage');
}

// Verify user with retry for clock sync issues
async function verifyUserWithRetry(token, retryCount = 0, maxRetries = 2) {
  try {
    return await checkUserExistsInBackend(token);
  } catch (err) {
    if (err.message && err.message.includes('Token used too early') && retryCount < maxRetries) {
      const waitTime = 1500 * Math.pow(2, retryCount);
      await new Promise((resolve) => setTimeout(resolve, waitTime));
      return await verifyUserWithRetry(token, retryCount + 1, maxRetries);
    }
    return { exists: false, error: err.message };
  }
}

async function checkUserExistsInBackend(token) {
  try {
    const checkUrl = `${process.env.NEXT_PUBLIC_BACKEND_URL}/check_user_created`;
    const response = await fetchWithToken(checkUrl, {
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-store',
      },
    });

    if (!response.ok) {
      let errorText;
      try {
        const errorJson = await response.json();
        errorText = JSON.stringify(errorJson);
      } catch {
        errorText = await response.text();
      }

      if (errorText.includes('Token used too early')) {
        throw new Error(`Token used too early: ${errorText}`);
      }

      throw new Error(`Backend verification failed: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    return { exists: data.exists, error: null };
  } catch (err) {
    console.error('Error checking user in backend:', err.message);
    throw err;
  }
}

function checkPurchaseIncludes(purchases, itemId, isCollection = false) {
  return (purchases || []).some((p) => {
    if (typeof p === 'string') {
      if (isCollection) {
        return p === itemId || p === `collection:${itemId}`;
      }
      return p === itemId.toString();
    }
    if (typeof p === 'number') {
      return p.toString() === itemId.toString();
    }
    if (p && typeof p === 'object') {
      if (isCollection) {
        return (
          p.collection === itemId ||
          p.name === itemId ||
          (p.type === 'collection' && p.id === itemId)
        );
      }
      return p.id === itemId || p.assetId === itemId;
    }
    return false;
  });
}

export default useUserStore;